import {
  AppError,
  CommonErrorsKey,
  TokenListItem,
  UnknownError,
  isSupportedChainId,
} from "@eulerxyz/core-sdk"
import type { Config } from "@wagmi/core"
import type { ChainIdParameter } from "@wagmi/core/internal"
import { type Evaluate, getAddress, isAddress, zeroAddress } from "viem"

interface ApiToken {
  chainId: number
  address: string
  name: string
  symbol: string
  decimals: number
  logoURI?: string
  metadata?: {
    [key: string]: any
  }
}

const EULER_API_URL =
  process.env.NEXT_PUBLIC_EULER_API_URL || "https://index-dev.euler.finance"

export type GetTokenListParameters<config extends Config = Config> = Evaluate<
  ChainIdParameter<config>
> & { all?: boolean }

export type GetTokenListReturnType = TokenListItem[]

export type GetTokenListErrorType = Error

export type TokenListPicker = TokenListItem[] | null | undefined

export async function getTokenList<config extends Config = Config>({
  chainId,
}: GetTokenListParameters<config>): Promise<TokenListItem[]> {
  if (!chainId || !isSupportedChainId(chainId)) {
    throw new AppError(
      CommonErrorsKey.ChainNotSupported,
      "Unsupported chain ID",
    )
  }

  try {
    const url = `${EULER_API_URL}/v1/tokens?chainId=${chainId}`
    // console.log('> getTokenList - isServer:', isServer, 'url:', url, 'chainId:', chainId)
    const response = await fetch(url)

    // console.log('RESPONSE -getTokenList - response status:', response.status, response.ok)

    if (!response.ok) {
      throw new UnknownError(
        CommonErrorsKey.TokenListNotFound,
        `Failed to fetch token list: ${response.status} ${response.statusText}`,
      )
    }

    const apiTokenList: ApiToken[] = await response.json()
    // console.log('JSON - getTokenList - tokenList length:', tokenList?.length, 'first item:', tokenList?.[0])

    if (!apiTokenList || !Array.isArray(apiTokenList)) {
      throw new UnknownError(
        CommonErrorsKey.TokenListNotFound,
        "Invalid token list response",
      )
    }
    const formattedTokenList: TokenListItem[] = []
    // ADD unitOfAccount to the token list
    formattedTokenList.push({
      chainId: chainId,
      decimals: 6,
      name: "USD",
      symbol: "USD",
      addressInfo: "0x0000000000000000000000000000000000000348",
      logoURI: "",
    })

    formattedTokenList.push({
      chainId: chainId,
      decimals: 18,
      name: "BTC",
      symbol: "BTC",
      addressInfo: "0xbBbBBBBbbBBBbbbBbbBbbbbBBbBbbbbBbBbbBBbB",
      logoURI: "",
    })

    apiTokenList.forEach((token: ApiToken) => {
      const address = token.address
      if (address) {
        const normalizedAddress = isAddress(address)
          ? getAddress(address)
          : zeroAddress

        // Merge logoURI from fallback if API token doesn't have it
        const formattedToken: TokenListItem = {
          chainId: token.chainId,
          decimals: token.decimals,
          name: token.name,
          symbol: token.symbol,
          addressInfo: normalizedAddress,
          logoURI: token.logoURI || "",
          meta: token.metadata
            ? {
                ...token.metadata,
              }
            : undefined,
        }
        formattedTokenList.push(formattedToken)
      }
    })
    return formattedTokenList
  } catch (err) {
    throw new UnknownError(
      CommonErrorsKey.TokenListNotFound,
      `Error loading token list: ${err}`,
    )
  }
}
